// Settings → Security. Every role has this (docs/spec/11-account-security.md).
//
// Two things about it are worth knowing before reading the code.
//
// FIRST, THIS IS ALSO THE FIRST CHANGE-PASSWORD SCREEN DAYBOARD HAS EVER HAD.
// POST /api/me/password has existed since phase 1 and nothing in the web app
// ever called it, so until now the only way to change a password was to
// pretend you had forgotten it. That gap is closed here.
//
// SECOND, THE RECOVERY CODES ARE SHOWN ONCE AND THE PAGE SAYS SO LOUDLY.
// There is no "show them again" — they are hashed the moment they are issued,
// so nothing in the system can produce them a second time. A screen that let
// somebody click past them without noticing would be handing them a lockout in
// three months' time.
import { useCallback, useEffect, useState } from 'react';
import { PasskeysPanel } from '../components/security/PasskeysPanel';
import QRCode from 'qrcode';
import { ApiError, api, type SecurityStatus } from '../api';
import { useSession } from '../session';
import { Banner, Field } from '../ui';

export function SecurityPage() {
  const { user, refresh: refreshSession } = useSession();
  const [status, setStatus] = useState<SecurityStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await api.security());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your security settings.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!status) {
    return (
      <section>
        <h1>Security</h1>
        {error ? <Banner kind="error">{error}</Banner> : <p>Loading…</p>}
      </section>
    );
  }

  return (
    <section>
      <h1>Security</h1>
      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      {/* Mounted only once the session has landed: the panel seeds its input
          from the CURRENT name at mount, and mounting it against a session
          that is still loading would seed an empty box. Inside AppShell the
          user always exists, so this guard is for the panel's own honesty. */}
      {user && (
        <NamePanel
          currentName={user.name}
          onChanged={(name) => {
            setNotice(`You are now ${name}.`);
            setError(null);
            // The sidebar footer and the More page both show the session's
            // name, so the session is refreshed rather than left to catch up
            // on the next full load.
            void refreshSession();
          }}
        />
      )}

      <TwoFactorPanel
        status={status}
        onChanged={(message) => {
          setNotice(message);
          setError(null);
          void load();
          void refreshSession();
        }}
      />

      <PasskeysPanel
        status={status}
        onChanged={(message) => {
          setNotice(message);
          setError(null);
          void load();
        }}
      />

      <PasswordPanel
        totpEnabled={status.totpEnabled}
        onChanged={() => {
          setNotice('Your password has been changed. Everywhere else has been signed out.');
          setError(null);
        }}
      />

      <EmailPanel
        currentEmail={user?.email ?? ''}
        totpEnabled={status.totpEnabled}
        onRequested={(address) => {
          setNotice(`Check ${address} for a link to confirm the change.`);
          setError(null);
        }}
      />
    </section>
  );
}

// --- Your name ----------------------------------------------------------

/**
 * The display name on your own account, which had no screen anywhere until
 * 2026-09-14: PATCH /api/me existed from the day the profile routes were
 * written and nothing ever called it — the same shape as round 33's unused
 * client PATCH. It lives on this page because Security is the one Settings
 * tab every role has, and the email address it sits beside is already here.
 *
 * Deliberately NO re-authentication: a display name is not a credential and
 * not a door — the server asks for nothing beyond the session, and asking for
 * a password here would teach people to type it for trivia.
 */
function NamePanel({
  currentName,
  onChanged,
}: {
  currentName: string;
  onChanged: (name: string) => void;
}) {
  const [name, setName] = useState(currentName);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const result = await api.updateMe({ name });
      setName(result.user.name);
      onChanged(result.user.name);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not change your name.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Your name</h2>
      {error && <Banner kind="error">{error}</Banner>}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="row">
          <Field
            label="Name"
            name="profileName"
            value={name}
            onChange={setName}
            error={fields.name}
            autoComplete="name"
          />
          <button type="submit" disabled={busy || name.trim() === currentName}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
      <p className="hint">
        How you appear across the app: on posts, forms and the schedule. Notes and log
        entries you have already written keep the name they were written under.
      </p>
    </section>
  );
}

/**
 * The password and code fields every sensitive change asks for.
 *
 * One component so the wording, the autocomplete hints and the "which of these
 * do we even need" decision are made once. `totpCode` is omitted from the
 * request entirely when the account has no second factor, rather than sent
 * empty — the server distinguishes the two.
 */
function ReauthFields({
  password,
  setPassword,
  code,
  setCode,
  totpEnabled,
  fields,
  idPrefix,
}: {
  password: string;
  setPassword: (value: string) => void;
  code: string;
  setCode: (value: string) => void;
  totpEnabled: boolean;
  fields: Record<string, string>;
  idPrefix: string;
}) {
  return (
    <>
      <Field
        label="Your current password"
        name={`${idPrefix}-currentPassword`}
        type="password"
        value={password}
        onChange={setPassword}
        error={fields.currentPassword}
        autoComplete="current-password"
      />
      {totpEnabled && (
        <Field
          label="Code from your authenticator app"
          name={`${idPrefix}-totpCode`}
          value={code}
          onChange={setCode}
          error={fields.totpCode}
          autoComplete="one-time-code"
          inputMode="numeric"
          autoCapitalize="none"
          spellCheck={false}
        />
      )}
    </>
  );
}

// --- Two-factor authentication ----------------------------------------

/** Exported so the enrolment interstitial can show exactly this and nothing
 *  else — two enrolment flows would be two places for the wording to drift. */
export function TwoFactorPanel({
  status,
  onChanged,
}: {
  status: SecurityStatus;
  onChanged: (message: string) => void;
}) {
  const [step, setStep] = useState<'idle' | 'setup' | 'codes'>('idle');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [proof, setProof] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { user } = useSession();
  const isAdmin = user?.role === 'admin';

  function reauth() {
    return status.totpEnabled ? { currentPassword: password, totpCode } : { currentPassword: password };
  }

  function fail(err: unknown, fallback: string) {
    if (err instanceof ApiError) {
      setError(err.message);
      setFields(err.fields);
    } else {
      setError(fallback);
    }
  }

  function reset() {
    setStep('idle');
    setPassword('');
    setTotpCode('');
    setProof('');
    setSecret('');
    setQr('');
    setFields({});
    setError(null);
  }

  async function startSetup() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const result = await api.totpSetup(reauth());
      setSecret(result.secret);
      // Rendered in the browser rather than fetched: the URI contains the
      // secret, and asking an image service for it would be handing the
      // second factor to a third party.
      setQr(await QRCode.toDataURL(result.otpauthUri, { margin: 1, width: 220 }));
      setStep('setup');
    } catch (err) {
      fail(err, 'Could not start setting that up.');
    } finally {
      setBusy(false);
    }
  }

  async function finishSetup() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const result = await api.totpEnable({ ...reauth(), code: proof });
      setCodes(result.recoveryCodes);
      setStep('codes');
    } catch (err) {
      fail(err, 'Could not turn that on.');
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await api.totpDisable(reauth());
      reset();
      onChanged('Two-factor authentication is off.');
    } catch (err) {
      fail(err, 'Could not turn that off.');
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const result = await api.regenerateRecoveryCodes(reauth());
      setCodes(result.recoveryCodes);
      setStep('codes');
    } catch (err) {
      fail(err, 'Could not generate new codes.');
    } finally {
      setBusy(false);
    }
  }

  if (step === 'codes') {
    return (
      <section className="panel">
        <h2>Save your recovery codes</h2>
        <Banner kind="error">
          This is the only time these will be shown. Write them down or print them, and keep
          them somewhere that is not your phone.
        </Banner>
        <p>
          Each code signs you in once, if you ever lose your phone. Without them, getting back
          in takes three days.
        </p>
        <ul className="recovery-codes">
          {codes.map((code) => (
            <li key={code}>
              <code>{code}</code>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => {
            reset();
            setCodes([]);
            onChanged('Two-factor authentication is set up.');
          }}
        >
          I have saved them
        </button>
      </section>
    );
  }

  if (step === 'setup') {
    return (
      <section className="panel">
        <h2>Scan this with your authenticator app</h2>
        <p>
          Use Google Authenticator, Microsoft Authenticator, 1Password, or whichever app you
          already have. Then type the six-digit code it shows.
        </p>
        {qr && <img src={qr} alt="QR code for your authenticator app" width={220} height={220} />}
        <p className="hint">
          Cannot scan it? Type this key in by hand instead: <code>{secret}</code>
        </p>
        {error && <Banner kind="error">{error}</Banner>}
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void finishSetup();
          }}
        >
          <Field
            label="Six-digit code"
            name="totp-proof"
            value={proof}
            onChange={setProof}
            error={fields.code}
            autoComplete="one-time-code"
            inputMode="numeric"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="go"
          />
          <div className="row">
            <button type="submit" disabled={busy}>
              {busy ? 'Checking…' : 'Turn it on'}
            </button>
            <button type="button" className="secondary" onClick={reset} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      </section>
    );
  }

  const low = status.totpEnabled && status.recoveryCodesRemaining <= status.recoveryCodesLow;

  return (
    <section className="panel">
      <h2>Two-factor authentication</h2>
      {error && <Banner kind="error">{error}</Banner>}

      {status.totpEnabled ? (
        <>
          <p>
            On. You are asked for a code from your authenticator app each time you sign in.
          </p>
          <p className={low ? 'field-error' : 'hint'}>
            {status.recoveryCodesRemaining} recovery{' '}
            {status.recoveryCodesRemaining === 1 ? 'code' : 'codes'} left
            {low ? '. Generate a new set before you run out.' : '.'}
          </p>
        </>
      ) : (
        // THREE statements, not two, and the middle one is the point (round
        // 38's review). "Required" is now only said where something actually
        // requires it — but an administrator on an account that does not
        // enforce it is still the person with the most to lose, and dropping
        // them into the same sentence as a field worker would have quietly
        // spent the strongest nudge in the app. Off, strongly advised, or
        // genuinely required.
        <p>
          {status.totpRequired
            ? 'Required on administrator accounts. Your account can see every job, client and document in the company, so it needs more than a password.'
            : isAdmin
              ? 'Off, and strongly recommended. Your account can see every job, client and document in the company, so it is worth more than a password; you can switch it off again whenever you like.'
              : 'Off. Turning it on means a code from your phone as well as your password, worth it if you can see anything you would not want a stranger reading.'}
        </p>
      )}

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void startSetup();
        }}
      >
        <ReauthFields
          password={password}
          setPassword={setPassword}
          code={totpCode}
          setCode={setTotpCode}
          totpEnabled={status.totpEnabled}
          fields={fields}
          idPrefix="totp"
        />
        <div className="row">
          <button type="submit" disabled={busy}>
            {status.totpEnabled ? 'Set up a new authenticator app' : 'Turn on two-factor'}
          </button>
          {status.totpEnabled && (
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => void regenerate()}
            >
              New recovery codes
            </button>
          )}
          {status.totpEnabled && !status.totpRequired && (
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => void disable()}
            >
              Turn it off
            </button>
          )}
        </div>
      </form>

      {status.totpEnabled && status.totpRequired && (
        <p className="hint">
          Administrator accounts cannot switch this off. If you have changed phones, set up a
          new authenticator app above; the old one stops working straight away.
        </p>
      )}
    </section>
  );
}

// --- Password ----------------------------------------------------------

function PasswordPanel({
  totpEnabled,
  onChanged,
}: {
  totpEnabled: boolean;
  onChanged: () => void;
}) {
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await api.changePassword({
        currentPassword: password,
        ...(totpEnabled ? { totpCode } : {}),
        newPassword,
      });
      setPassword('');
      setTotpCode('');
      setNewPassword('');
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not change your password.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Password</h2>
      {error && <Banner kind="error">{error}</Banner>}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <ReauthFields
          password={password}
          setPassword={setPassword}
          code={totpCode}
          setCode={setTotpCode}
          totpEnabled={totpEnabled}
          fields={fields}
          idPrefix="password"
        />
        <Field
          label="New password"
          name="newPassword"
          type="password"
          value={newPassword}
          onChange={setNewPassword}
          error={fields.newPassword}
          autoComplete="new-password"
          // Says the rule that is actually enforced, and the one piece of
          // advice worth giving. No character-class nagging: NIST asks us not
          // to, and it produces worse passwords.
          hint="At least 10 characters. A few unrelated words beats a short one with symbols in it."
          enterKeyHint="go"
        />
        <button type="submit" disabled={busy}>
          {busy ? 'Changing…' : 'Change password'}
        </button>
      </form>
      <p className="hint">
        Changing it signs out every other browser and phone you are signed in on.
      </p>
    </section>
  );
}

// --- Email address ------------------------------------------------------

function EmailPanel({
  currentEmail,
  totpEnabled,
  onRequested,
}: {
  currentEmail: string;
  totpEnabled: boolean;
  onRequested: (address: string) => void;
}) {
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [email, setEmail] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const result = await api.requestEmailChange({
        currentPassword: password,
        ...(totpEnabled ? { totpCode } : {}),
        email,
      });
      setPassword('');
      setTotpCode('');
      setEmail('');
      onRequested(result.pendingEmail);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not start that change.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Email address</h2>
      <p>
        You sign in with <strong>{currentEmail}</strong>, and password reset links go there.
      </p>
      {error && <Banner kind="error">{error}</Banner>}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Field
          label="New email address"
          name="newEmail"
          type="email"
          value={email}
          onChange={setEmail}
          error={fields.email}
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
        />
        <ReauthFields
          password={password}
          setPassword={setPassword}
          code={totpCode}
          setCode={setTotpCode}
          totpEnabled={totpEnabled}
          fields={fields}
          idPrefix="email"
        />
        <button type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Send a confirmation link'}
        </button>
      </form>
      <p className="hint">
        Nothing changes until the new address confirms it, and we will let{' '}
        {currentEmail} know it was asked for.
      </p>
    </section>
  );
}
